// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import { Annotation, isBoxAnnotation, isTextAnnotation } from './annotations';

import styles from './AnnotationOverlay.module.css';

export interface AnnotationOverlayProps {
  /**
   * Annotations to render, in source order.  The overlay paints in
   * array order, so callers can encode z-stacking by ordering this
   * array (last item paints on top).
   */
  annotations: ReadonlyArray<Annotation>;
}

/**
 * D3 acceptance criterion 28 -- annotation overlay layer.
 *
 * Renders a stack of annotations as a single SVG `<g>` group, intended
 * to be the last child of Canvas's zoom-transformed group so it paints
 * on top of every SFD/CLD render layer while still inheriting pan and
 * zoom for free.
 *
 * Two invariants guard the "without modifying the underlying model
 * data" half of the criterion:
 *
 *   1. **No model imports.**  This file does not import any engine
 *      type (`Project`, `Model`, `ViewElement`, etc.) and never
 *      receives them as props.  The compile-time check is enforced
 *      by the test
 *      [tests/annotation-overlay-readonly.test.ts](../tests/annotation-overlay-readonly.test.ts).
 *
 *   2. **No mutation callbacks.**  This component takes no
 *      `onSelection` / `onMove*` / `onPatch` callbacks.  Pointer
 *      events pass through (`pointer-events: none` on the root
 *      group), so clicks land on the diagram element underneath,
 *      not on the annotation.  Editable annotations (planned for a
 *      later D3 criterion) will need their own component with an
 *      explicit pointer-events policy and a callback contract that
 *      goes to a separate annotation state slice -- never to the
 *      engine patch pipeline.
 */
export const AnnotationOverlay = React.memo(function AnnotationOverlay(
  props: AnnotationOverlayProps,
): React.ReactElement | null {
  const { annotations } = props;

  if (annotations.length === 0) {
    return null;
  }

  return (
    <g className={clsx(styles.overlay, 'simlin-annotation-overlay')}>
      {annotations.map(renderAnnotation)}
    </g>
  );
});

function renderAnnotation(a: Annotation): React.ReactElement | null {
  if (isTextAnnotation(a)) {
    return (
      <text
        key={a.uid}
        className={clsx(styles.text, 'simlin-annotation-text')}
        x={a.x}
        y={a.y}
      >
        {a.text}
      </text>
    );
  }
  if (isBoxAnnotation(a)) {
    return (
      <rect
        key={a.uid}
        className={clsx(styles.box, 'simlin-annotation-box')}
        x={a.x}
        y={a.y}
        width={a.width}
        height={a.height}
      />
    );
  }
  return null;
}
